/**
 * BullMQ queue names and job payload schemas.
 *
 * Payloads are Zod-validated at both ends: the producer parses before `add()`
 * and the consumer parses on receipt, so a malformed job fails loudly at the
 * boundary instead of throwing somewhere deep inside a processor.
 */

import { z } from 'zod';

export const QUEUE_NAMES = {
  AI_GRADE: 'ai-grade',
  ANSWER_REVIEW: 'answer-review',
  REPORT_GENERATION: 'report-generation',
  CUSTOM_PAPER_EXTRACT: 'custom-paper-extract',
  NOTIFICATIONS: 'notifications',
  STUDY_PLAN: 'study-plan',
  COMMON_ERRORS: 'common-errors',
} as const;

export type QueueName = (typeof QUEUE_NAMES)[keyof typeof QUEUE_NAMES];

/**
 * Bounded retry for jobs that call the paid AI provider: one automatic retry
 * with backoff rides out a transient Gemini/network blip instead of silently
 * dropping a grade to manual review. Safe because the processors short-circuit
 * on already-done state, so a re-run never double-finalizes or double-pays.
 */
export const AI_JOB_RETRY = {
  attempts: 2,
  backoff: { type: 'exponential' as const, delay: 5_000 },
} as const;

/** Keep a day of terminal jobs for debugging, then let Redis reclaim them. */
export const JOB_RETENTION = {
  removeOnComplete: { age: 86_400 },
  removeOnFail: { age: 86_400 },
} as const;

/**
 * Every payload carries its tenant.
 *
 * Not for convenience — the consumer loads the job's ROOT RECORD and compares
 * that record's tenant against this field before doing any paid or destructive
 * work, rejecting a mismatch without processing. A payload that could not state
 * its tenant would leave the worker with nothing to compare against, and a
 * replayed or hand-crafted job could then be made to finalize an attempt in a
 * workspace it does not belong to.
 */
const tenantScoped = { tenantId: z.string().min(1) };

// The chosen bin and reviewer are read from PostgreSQL, never trusted from Redis.
export const answerReviewJobSchema = z.object({
  ...tenantScoped,
  attemptId: z.string().min(1),
});
export type AnswerReviewJobData = z.infer<typeof answerReviewJobSchema>;

export const aiGradeJobSchema = z.object({
  ...tenantScoped,
  studentPaperId: z.string().min(1),
  questionId: z.string().min(1),
  attemptIndex: z.number().int().min(1).max(3),
  submittedAnswer: z.string().nullable(),
  answerMethod: z.enum(['OPTION', 'SCAN', 'TYPED', 'AUDIO']),
  s3Key: z.string().optional(),
});
export type AiGradeJobData = z.infer<typeof aiGradeJobSchema>;

export const reportJobSchema = z.object({
  ...tenantScoped,
  studentPaperId: z.string().min(1),
  studentId: z.string().min(1),
});
export type ReportJobData = z.infer<typeof reportJobSchema>;

export const customPaperExtractJobSchema = z.object({
  ...tenantScoped,
  customPaperId: z.string().min(1),
});
export type CustomPaperExtractJobData = z.infer<typeof customPaperExtractJobSchema>;

/** Cron-driven jobs carry no payload; the job *name* selects the processor. */
export const emptyJobSchema = z.object({}).passthrough();

export const NOTIFICATION_JOBS = {
  DAILY_DUE_CARDS: 'daily-due-cards',
  FORMULA_BRUSHUP: 'formula-brushup',
  PARENT_DIGEST: 'parent-weekly-digest',
  WEEKLY_SHEET: 'weekly-sheet',
} as const;

export const STUDY_PLAN_JOB = 'daily-study-plan' as const;

/**
 * Practice-topic common errors. The nightly SWEEP finds every (tenant, topic)
 * with new practice data and enqueues one REFRESH per pair, so a model call
 * that fails is retried on its own instead of re-running the whole night.
 */
export const COMMON_ERRORS_JOBS = {
  SWEEP: 'common-errors-sweep',
  REFRESH_TOPIC: 'common-errors-refresh-topic',
} as const;

/** One topic at one school. `conceptKey` is the TOPIC's, year-independent. */
export const topicCommonErrorsJobSchema = z.object({
  ...tenantScoped,
  conceptKey: z.string().min(1).max(400),
});
export type TopicCommonErrorsJobData = z.infer<typeof topicCommonErrorsJobSchema>;

/** Discriminator for `Job` rows, mirroring the queue that produced them. */
export const JOB_TYPES = ['AI_GRADE', 'REPORT_GENERATION', 'CUSTOM_PAPER_EXTRACT'] as const;
export type JobType = (typeof JOB_TYPES)[number];

export const JOB_STATES = ['waiting', 'active', 'completed', 'failed'] as const;
export type JobState = (typeof JOB_STATES)[number];

/** Redis pub/sub channel carrying worker → API events for SSE relay. */
export const SSE_CHANNEL = 'cwy:sse:student-paper';

export const sseMessageSchema = z.object({
  studentPaperId: z.string().min(1),
  event: z.enum(['question-graded', 'answer-review', 'report-ready']),
  payload: z.record(z.string(), z.unknown()),
});
export type SseMessage = z.infer<typeof sseMessageSchema>;
